import React, { useRef, useEffect, useState } from "react";
import { Box, Typography, FormControl, Select, MenuItem } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router";
import { useLazyQuery } from "@apollo/client";
import { GET_EVENT_BY_ID } from "src/graphql/queries/events";

import TableInventory from "src/components/floor-plan/TableInventory";
import CanvasActionButton from "src/components/floor-plan/CanvasActionButton";
import CanvasStage from "src/components/floor-plan/CanvasStage";
import { PrimaryButton, SecondaryButton } from "../../events/listing-details/listingButtons";

import useFloorPlanState from "src/hooks/useFloorPlanState";
import { clearSelectTransform, handleFileChange } from "src/utils/canvasHandlers";
import FormProvider, { RHFToggleButtonGroup } from "src/components/hook-form";
import { GET_FLOORPLANS } from "src/graphql/queries/floorplans";
import { UPSERT_FLOORPLAN } from "src/graphql/mutations/floorplans";
import { useMutationWithAuth } from "src/graphql/instances";
import { useSnackbar } from "notistack";
import { SET_MEDIA_PUBLIC, UPLOAD_MEDIA } from "src/graphql/mutations/media";
import MobileIcon from "src/assets/icons/floor-plan/MobileIcon";
import DesktopIcon from "src/assets/icons/floor-plan/DesktopIcon";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "src/hooks/useAuthContext";

const entranceArea = { id: "Entrance", name: "Entrance" };

const FloorPlanBuilder = () => {
  const { id: listingId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const { user } = useAuthContext();
  const isAdmin = user.role === "admin";

  const containerRef = useRef(null);

  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });

  const [getEventById] = useLazyQuery(GET_EVENT_BY_ID, {
    fetchPolicy: "network-only",
    context: { headers: { Authorization: localStorage.getItem("accessToken") } },
  });

  const [getFloorPlans] = useLazyQuery(GET_FLOORPLANS, {
    fetchPolicy: "cache-and-network",
    context: { headers: { Authorization: localStorage.getItem("accessToken") } },
  });

  const [upsertFloorPlans] = useMutationWithAuth(UPSERT_FLOORPLAN);
  const [uploadMedia] = useMutationWithAuth(UPLOAD_MEDIA);
  const [setMediaPublic] = useMutationWithAuth(SET_MEDIA_PUBLIC);

  const {
    booking,
    selectedArea,
    setSelectedArea,
    methods,
    viewportMethods,
    enabledPlanMethods,
    isManagementSection,
    isViewportMobile,
    tablesInventory,
    customerTablesInventory,
    customerAreasInventory,
    floorPlans,
    setFloorPlans,
    customerPlans,
    setCustomerPlans,
    ...floorHandlers
  } = useFloorPlanState({ getEventById, getFloorPlans, upsertFloorPlans, listingId, stageSize });

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setStageSize({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        });
      }
    };
    const resizeObserver = new ResizeObserver(updateSize);
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const handleChangeArea = (e) => {
    clearSelectTransform(floorHandlers.setSelectedIds, floorHandlers.transformerRef)
    setSelectedArea(e.target.value);
  };

  const allAreas = !isManagementSection
    ? [entranceArea, ...(booking?.areas || [])]
    : booking?.areas || [];


  return (
    <Box display="flex" flex={1} gap={4} p={2} sx={{ height: "calc(100vh - 73px)" }}>
      <Box display="flex" flexDirection="column" flex={0.2}>
        <Typography variant="h5" sx={{ mb: 1 }}>Area</Typography>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <Select
            value={selectedArea}
            onChange={handleChangeArea}
          >
            {allAreas.map((area) => (
              <MenuItem key={area.id} value={area}>{area.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="h5" sx={{ mb: 1 }}>Available Items</Typography>
        <TableInventory
          isManagementSection={isManagementSection}
          tablesInventory={isManagementSection ? tablesInventory : customerTablesInventory}
          customerAreasInventory={customerAreasInventory}
          selectedArea={selectedArea}
          onAddTable={floorHandlers.addTableToPlan}
          onAddButton={floorHandlers.addButtonToPlan}
        />

        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <SecondaryButton sx={{ flex: 1 }} onClick={floorHandlers.addAllTables}>
            Add All
          </SecondaryButton>
          <SecondaryButton sx={{ flex: 1 }} onClick={floorHandlers.removeAllTables}>
            Remove All
          </SecondaryButton>
        </Box>

        <Box mt="auto">
          <SecondaryButton fullWidth onClick={() => navigate(-1)} sx={{ mb: 2 }}>
            Exit Editor
          </SecondaryButton>
          <PrimaryButton fullWidth onClick={floorHandlers.saveFloorPlan}>
            Save Changes
          </PrimaryButton>
        </Box>
      </Box>

      <Box ref={containerRef} position="relative" flex={1} borderRadius={2} bgcolor={theme.palette.background.paper}>
        <CanvasStage
          isManagementSection={isManagementSection}
          isViewportMobile={isViewportMobile}
          stageSize={stageSize}
          floorPlans={isManagementSection ? floorPlans : customerPlans}
          setFloorPlans={isManagementSection ? setFloorPlans : setCustomerPlans}
          selectedArea={selectedArea}
          {...floorHandlers}
        />

        {floorHandlers.canvasActions.map((action, index) => (
          <CanvasActionButton key={action.actionName} i={index} {...action} mode={floorHandlers.mode} />
        ))}
        {isAdmin && <FormProvider methods={methods}>
          <Box
            position="absolute"
            right={24}
            top={24}
            zIndex={999}
          >
            <RHFToggleButtonGroup
              sx={{ width: "fit-content" }}
              name={"managementSection"}
              options={
                [
                  {
                    label: "Management View",
                    value: true,
                  },
                  {
                    label: "Customer View",
                    value: false,
                  },
                ] || []
              }
              exclusive
            />
          </Box>
        </FormProvider>}
        {!isManagementSection && <FormProvider methods={enabledPlanMethods}>
          <Box
            position="absolute"
            right={24}
            top={isAdmin ? 96 : 24}
            zIndex={999}
          >
            <RHFToggleButtonGroup
              sx={{ width: "fit-content" }}
              name={"enabledCustomerPlan"}
              options={
                [
                  {
                    label: t('bookings.enabled'),
                    value: true,
                  },
                  {
                    label: t('bookings.disabled'),
                    value: false,
                  },
                ] || []
              }
              exclusive
            />
          </Box>
        </FormProvider>}
        {!isManagementSection && <FormProvider methods={viewportMethods}>
          <Box
            position="absolute"
            right={24}
            top={isAdmin ? 168 : 96}
            zIndex={999}
          >
            <RHFToggleButtonGroup
              sx={{ width: "fit-content" }}
              name={"viewportMobile"}
              options={
                [
                  {
                    label: <MobileIcon />,
                    value: true,
                  },
                  {
                    label: <DesktopIcon />,
                    value: false,
                  },
                ] || []
              }
              exclusive
            />
          </Box>
        </FormProvider>}
        <input
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          ref={floorHandlers.fileInputRef}
          onChange={(e) => handleFileChange(e, selectedArea, setCustomerPlans, enqueueSnackbar, uploadMedia, setMediaPublic)}
        />
      </Box>
    </Box>
  );
};

export default FloorPlanBuilder;

